import { blobToBase64 } from '../lib/image';
import type { ItemRecord } from '../types';
import { applyBackgroundPatch } from './items';
import { getSyncConfig } from './settings';

export async function suggestItemNames(item: ItemRecord) {
  const config = await getSyncConfig();
  if (!config.endpoint || !config.token || item.name.trim()) return null;
  try {
    const result = await callWorker<{ suggestions?: string[] }>(config, '/ai/suggest', {
      base64Image: await blobToBase64(item.photo)
    });
    const suggestions = cleanStrings(result.suggestions).slice(0, 10);
    if (!suggestions.length) return null;
    return applyBackgroundPatch(item.id, item.updatedAt, { aiSuggestions: suggestions });
  } catch {
    return null;
  }
}

export async function enrichItem(item: ItemRecord) {
  const config = await getSyncConfig();
  if (!config.endpoint || !config.token || !item.name.trim()) return null;

  const base64Image = await blobToBase64(item.photo).catch(() => '');
  const result = await callWorker<{ synonyms?: string[] }>(config, '/ai/synonyms', {
    name: item.name,
    locationNote: item.locationNote,
    base64Image,
    imageMimeType: item.photoMimeType
  }).catch(() => null);
  const suggestions = cleanStrings(result?.synonyms).slice(0, 10);
  if (!suggestions.length) return null;
  return applyBackgroundPatch(item.id, item.updatedAt, {
    aiSuggestions: suggestions,
    status: item.locationNote.trim() ? 'ready' : 'needs_location'
  });
}

export async function suggestLocationGroup(locationNote: string, existingGroups: string[]) {
  const config = await getSyncConfig();
  if (!config.endpoint || !config.token || !locationNote.trim()) return '';
  try {
    const result = await callWorker<{ locationGroup?: string }>(config, '/ai/location-group', {
      locationNote: locationNote.trim(),
      existingGroups
    });
    return result.locationGroup?.trim() ?? '';
  } catch {
    return '';
  }
}

async function callWorker<T>(config: { endpoint: string; token: string }, path: string, body: unknown): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 12_000);
  try {
    const response = await fetch(`${config.endpoint}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${config.token}` },
      body: JSON.stringify(body),
      signal: controller.signal
    });
    const json = await response.json() as T & { error?: string };
    if (!response.ok) throw new Error(json.error || `AI request failed with ${response.status}.`);
    return json;
  } finally {
    clearTimeout(timeout);
  }
}

function cleanStrings(value: unknown) {
  if (!Array.isArray(value)) return [];
  const strings = new Set<string>();
  for (const entry of value) {
    if (typeof entry !== 'string') continue;
    const cleaned = entry.trim();
    if (cleaned) strings.add(cleaned);
  }
  return [...strings];
}
